import { Dialog } from '../../../components/ui';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';

import { classroomDashboard } from '../../../services/organization/classroom/classroom.service';
import {
  getChannelMessagesApi,
  registerStreamUser,
  sendChannelMessageApi,
  uploadChannelMediaMessageApi,
  deleteMessageApi,
  editMessageApi,
} from '../../../services/getstream/getstream.service';
import { subscribeCommunityChannelRealtime } from '../../../services/getstream/streamRealtime';
import BitmovinPlayer from '../../../components/common/BitmovinPlayer';
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';

function getAttachmentKind(attachment) {
  const type = String(attachment?.type || '').toLowerCase();
  const mime = String(attachment?.mime_type || '').toLowerCase();
  const name = String(attachment?.title || '').toLowerCase();

  if (type === 'image' || mime.startsWith('image/')) return 'image';
  if (type === 'video' || mime.startsWith('video/')) return 'video';
  if (type === 'audio' || mime.startsWith('audio/')) return 'audio';
  if (type === 'file' || type === 'document') return 'document';
  if (
    name.endsWith('.pdf') ||
    name.endsWith('.doc') ||
    name.endsWith('.docx') ||
    name.endsWith('.ppt') ||
    name.endsWith('.pptx') ||
    name.endsWith('.xls') ||
    name.endsWith('.xlsx') ||
    name.endsWith('.txt') ||
    name.endsWith('.csv') ||
    name.endsWith('.png') ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.gif') ||
    name.endsWith('.webp') ||
    name.endsWith('.mp4') ||
    name.endsWith('.mov') ||
    name.endsWith('.mp3') ||
    name.endsWith('.wav')
  ) {
    if (name.match(/\.(png|jpe?g|gif|webp)$/)) return 'image';
    if (name.match(/\.(mp4|mov|webm)$/)) return 'video';
    if (name.match(/\.(mp3|wav|m4a|aac|ogg)$/)) return 'audio';
    return 'document';
  }
  return 'document';
}

function resolveUploadType(pendingType, file) {
  if (pendingType) return pendingType;
  const mime = String(file?.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'document';
}

function preloadRemoteAttachment(url, kind) {
  if (!url || kind !== 'image') return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = resolve;
    img.onerror = resolve;
    img.src = url;
  });
}

function UploadOverlay({ progress }) {
  return (
    <div className="community-upload-overlay">
      <div className="community-upload-spinner" />
      {typeof progress === 'number' && progress > 0 ? <span>{progress}%</span> : null}
    </div>
  );
}

function CommunityVideoAttachment({ src, title, uploading }) {
  const [playing, setPlaying] = useState(false);

  if (uploading) {
    return (
      <video
        src={src}
        className="community-media-video"
        muted
        playsInline
        preload="metadata"
      />
    );
  }

  if (playing) {
    return (
      <BitmovinPlayer
        src={src}
        title={title || 'Community video'}
        className="community-media-video"
        objectFit="contain"
        style={{ width: '100%', height: '100%' }}
      />
    );
  }

  return (
    <button
      type="button"
      className="community-video-preview-btn"
      onClick={() => setPlaying(true)}
      aria-label="Play video"
    >
      <video src={src} className="community-media-video" muted playsInline preload="metadata" />
      <span className="community-video-play-overlay">
        <Icon icon="fluent:play-circle-24-filled" width="48" height="48" />
      </span>
    </button>
  );
}

function CommunityMessageAttachment({ attachment, mine, uploading, progress, onImageOpen }) {
  const previewUrl = attachment.preview_url || null;
  const remoteUrl = attachment.asset_url || null;
  const displayUrl =
    remoteUrl && /^https?:\/\//i.test(String(remoteUrl))
      ? remoteUrl
      : previewUrl || remoteUrl;
  const title = attachment.title || 'Attachment';
  const kind = getAttachmentKind(attachment);

  if (kind === 'image' && displayUrl) {
    return (
      <div className="tw-mb-2 community-media community-media-wrap">
        <img
          src={displayUrl}
          alt={title}
          className="community-media-img"
          onClick={() => {
            if (!uploading) onImageOpen(displayUrl, title);
          }}
          role="button"
          style={{
            cursor: uploading ? 'default' : 'pointer',
            opacity: uploading ? 0.92 : 1,
          }}
        />
        {uploading ? <UploadOverlay progress={progress} /> : null}
      </div>
    );
  }

  if (kind === 'video' && displayUrl) {
    return (
      <div className="tw-mb-2 community-media community-media-wrap">
        <div className="community-media-video-wrap">
          <CommunityVideoAttachment src={displayUrl} title={title} uploading={uploading} />
        </div>
        {uploading ? <UploadOverlay progress={progress} /> : null}
      </div>
    );
  }

  if (kind === 'audio' && displayUrl) {
    return (
      <div className="tw-mb-2 community-audio-wrap">
        <audio
          src={displayUrl}
          controls
          style={{ width: '100%', opacity: uploading ? 0.75 : 1 }}
        />
        {uploading ? <UploadOverlay progress={progress} /> : null}
      </div>
    );
  }

  const ext = (() => {
    const n = String(title || '').toLowerCase();
    const last = n.lastIndexOf('.');
    return last >= 0 ? n.slice(last + 1) : '';
  })();
  const iconForDoc = (() => {
    if (ext === 'pdf') return 'fluent:document-pdf-24-regular';
    if (ext === 'csv' || ext === 'xls' || ext === 'xlsx') return 'fluent:document-table-24-regular';
    if (ext === 'ppt' || ext === 'pptx') return 'fluent:document-briefcase-24-regular';
    if (ext === 'doc' || ext === 'docx') return 'fluent:document-text-24-regular';
    return 'fluent:document-24-regular';
  })();

  return (
    <div className="tw-mb-2 community-media-wrap">
      <a
        href={displayUrl || undefined}
        target="_blank"
        rel="noreferrer"
        className={`community-doc-card   ${mine ? "mine" : ""}`}
        onClick={(e) => {
          if (!displayUrl || uploading) e.preventDefault();
        }}
      >
        <div className="community-doc-icon">
          <Icon icon={iconForDoc} width="22" height="22" />
        </div>
        <div className="community-doc-meta">
          <div className="community-doc-title" title={title}>
            {title}
          </div>
          <div className="community-doc-sub">
            {ext ? ext.toUpperCase() : 'FILE'}
            {attachment.mime_type ? ` • ${attachment.mime_type}` : ''}
          </div>
        </div>
        {!uploading ? (
          <div className="community-doc-action" title="Open">
            <Icon icon="fluent:open-24-regular" width="20" height="20" />
          </div>
        ) : null}
      </a>
      {uploading ? <UploadOverlay progress={progress} /> : null}
    </div>
  );
}

function createLongPressHandlers(onLongPress, delayMs = 500) {
  let timer = null;
  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return {
    onPointerDown: (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      clearTimer();
      timer = setTimeout(() => {
        timer = null;
        onLongPress();
      }, delayMs);
    },
    onPointerUp: clearTimer,
    onPointerLeave: clearTimer,
    onPointerCancel: clearTimer,
  };
}

function Cummunity({ classroom_id, stream_channel_id: streamChannelIdProp }) {
  const { timeZone } = useTimezone();
  const [input, setInput] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [pendingUploadType, setPendingUploadType] = useState(null);
  const [channelId, setChannelId] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [viewer, setViewer] = useState({ open: false, url: null, title: '' });
  const [messages, setMessages] = useState(() => []);
  const [editingMessage, setEditingMessage] = useState(null);
  const [messageMenu, setMessageMenu] = useState(null);

  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamUnsubscribeRef = useRef(null);
  const inputRef = useRef(null);

  const headerProfile = useMemo(
    () => ({
      name: 'Community',
      imageUrl: null,
    }),
    []
  );
  const streamAppKey = useMemo(
    () => process.env.REACT_APP_GET_STREAM_APP_KEY || '',
    []
  );

  const formatTime = (iso) => {
    return formatUtcInTimeZone(iso, timeZone, 'hh:mm A');
  };

  const initials = (name) => {
    const n = (name || '').trim();
    if (!n) return '?';
    const parts = n.split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase()).join('');
  };

  const scrollToBottom = () => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  const isNearBottom = () => {
    const el = listRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  const isDeletedStreamMessage = (m) => {
    if (!m) return true;
    const type = String(m.type || '').toLowerCase();
    return type === 'deleted' || Boolean(m.deleted_at) || Boolean(m.deleted_for_me);
  };

  const normalizeApiMessage = (m, myId) => {
    if (isDeletedStreamMessage(m) || !m?.id) return null;
    return {
      id: m.id,
      senderName: m.user?.name || 'User',
      text: m.text || '',
      createdAt: m.created_at || new Date().toISOString(),
      mine: myId ? String(m.user?.id) === myId : false,
      attachments: m.attachments || [],
      edited: Boolean(m.message_text_updated_at),
    };
  };

  const normalizeStreamEventMessage = (m, myId) => {
    if (!m?.id || isDeletedStreamMessage(m)) return null;
    const senderId = m.user?.id ? String(m.user.id) : '';
    return {
      id: String(m.id),
      senderName: m.user?.name || 'User',
      text: m.text || '',
      createdAt: (m.created_at && String(m.created_at)) || new Date().toISOString(),
      mine: Boolean(myId && senderId === myId),
      attachments: m.attachments || [],
      edited: Boolean(m.message_text_updated_at),
    };
  };

  const isEphemeralLocalId = (id) => String(id || '').startsWith('local-') || String(id || '').startsWith('upload-');

  const pendingMessageMatchesServer = (pending, server) => {
    if (!pending?.mine || !server?.mine || !isEphemeralLocalId(pending.id)) return false;
    if ((pending.text || '').trim() !== (server.text || '').trim()) return false;

    const pendingAtt = pending.attachments?.[0];
    const serverAtt = server.attachments?.[0];
    if (!pendingAtt && !serverAtt) return true;
    if (!pendingAtt || !serverAtt) return false;
    return getAttachmentKind(pendingAtt) === getAttachmentKind(serverAtt);
  };

  const mergeMessageRow = (existing, incoming) => {
    const keepExistingAttachments =
      Array.isArray(existing?.attachments) &&
      existing.attachments.length > 0 &&
      JSON.stringify(existing.attachments) === JSON.stringify(incoming.attachments || []);

    return {
      ...existing,
      ...incoming,
      attachments: keepExistingAttachments ? existing.attachments : incoming.attachments,
      uploading: incoming.uploading ?? existing.uploading,
      progress: incoming.progress ?? existing.progress,
      previewUrl: incoming.previewUrl ?? existing.previewUrl,
    };
  };

  const integrateServerMessage = (prev, server, options = {}) => {
    const next = (prev || []).filter((m) => {
      if (m.id === server.id) return false;
      if (options.replaceOptimisticId && m.id === options.replaceOptimisticId) return false;
      if (pendingMessageMatchesServer(m, server)) return false;
      return true;
    });
    return [...next, server].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  };

  const mergeByIdNewestLast = (prev, incoming) => {
    let result = prev || [];
    (incoming || []).forEach((m) => {
      const existing = result.find((row) => row.id === m.id);
      if (existing?.uploading) {
        result = integrateServerMessage(result, m);
      } else if (existing) {
        result = result.map((row) => (row.id === m.id ? mergeMessageRow(row, m) : row));
      } else {
        result = integrateServerMessage(result, m);
      }
    });
    return result;
  };

  const getStoredUser = () => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  };

  const getMyId = () => {
    const u = getStoredUser();
    return u?.id ? String(u.id) : null;
  };

  const getMyDisplayName = () => {
    const u = getStoredUser();
    const name = (u?.name || u?.full_name || u?.username || '').trim();
    return name || 'User';
  };

  const cancelEdit = () => {
    setEditingMessage(null);
    setInput('');
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    if (!channelId) return;
    if (editingMessage) {
      handleSaveEdit();
      return;
    }

    // optimistic
    const optimisticId = `local-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: optimisticId,
        senderName: getMyDisplayName(),
        text,
        createdAt: new Date().toISOString(),
        mine: true,
      },
    ]);
    setInput('');
    setTimeout(scrollToBottom, 0);

    sendChannelMessageApi({ channelId, text })
      .then((res) => {
        const msg = res?.data?.message;
        if (!msg) return;
        const normalized = normalizeApiMessage(msg, getMyId());
        if (!normalized) return;
        setMessages((prev) =>
          integrateServerMessage(prev, normalized, { replaceOptimisticId: optimisticId })
        );
      })
      .catch(() => {
        // keep optimistic message; backend errors are handled globally in ApiController
      });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && editingMessage) {
      e.preventDefault();
      cancelEdit();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!messageId || String(messageId).startsWith('local-')) return;
    if (!channelId) return;

    try {
      await deleteMessageApi({ messageId, channelId });
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      if (editingMessage?.id === messageId) cancelEdit();
      setMessageMenu(null);
    } catch (err) {
      console.error('[Community] delete message failed:', err);
    }
  };

  const startEditMessage = (message) => {
    if (!message?.id || !String(message.text || '').trim()) return;
    setMessageMenu(null);
    setEditingMessage(message);
    setInput(message.text || '');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSaveEdit = async () => {
    const message = editingMessage;
    const text = input.trim();
    if (!message || !text || !channelId) return;
    if (text === String(message.text || '').trim()) {
      cancelEdit();
      return;
    }

    try {
      const res = await editMessageApi({ messageId: message.id, channelId, text });
      const returned = res?.data?.message;
      setMessages((prev) =>
        prev.map((item) =>
          item.id === message.id
            ? { ...item, text: returned?.text || text, edited: true }
            : item
        )
      );
      cancelEdit();
    } catch (err) {
      console.error('[Community] edit message failed:', err);
    }
  };

  const openMessageActions = (message) => {
    if (!message?.mine || message.uploading) return;
    if (String(message.id).startsWith('local-') || String(message.id).startsWith('upload-')) return;
    setMessageMenu({
      id: message.id,
      canEdit: Boolean(String(message.text || '').trim()),
      message,
      step: 'menu',
    });
  };

  const resizeInput = () => {
    const el = inputRef.current;
    if (!el) return;
    // reset then grow
    el.style.height = 'auto';
    const style = window.getComputedStyle(el);
    const lineHeight = Number.parseFloat(style.lineHeight || '20') || 20;
    const paddingY =
      (Number.parseFloat(style.paddingTop || '0') || 0) +
      (Number.parseFloat(style.paddingBottom || '0') || 0);
    const borderY =
      (Number.parseFloat(style.borderTopWidth || '0') || 0) +
      (Number.parseFloat(style.borderBottomWidth || '0') || 0);
    const maxHeight = lineHeight * 4 + paddingY + borderY; // up to 4 rows
    const next = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  useEffect(() => {
    resizeInput();
  }, [input]);

  const handleChooseUploadType = (type) => {
    // UI only. Next step: upload to S3 and send Stream attachment.
    setPendingUploadType(type);
    setIsUploadModalOpen(false);

    // open OS file picker
    setTimeout(() => {
      if (fileInputRef.current) fileInputRef.current.click();
    }, 0);
  };

  const getAcceptForUploadType = (type) => {
    switch (type) {
      case 'image':
        return 'image/*';
      case 'video':
        return 'video/*';
      case 'audio':
        return 'audio/*';
      case 'document':
        return '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv';
      default:
        return '*/*';
    }
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const type = resolveUploadType(pendingUploadType, file);
    if (!channelId) return;

    const previewUrl = URL.createObjectURL(file);
    const caption = input.trim();
    const optimisticId = `upload-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: optimisticId,
        senderName: getMyDisplayName(),
        text: caption,
        createdAt: new Date().toISOString(),
        mine: true,
        uploading: true,
        progress: 0,
        previewUrl,
        attachments: [
          {
            type,
            title: file.name,
            asset_url: previewUrl,
            mime_type: file.type || null,
          },
        ],
      },
    ]);
    setInput('');
    setPendingUploadType(null);
    setTimeout(scrollToBottom, 0);

    const revokePreview = () => {
      window.setTimeout(() => {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {
          // ignore
        }
      }, 300);
    };

    uploadChannelMediaMessageApi(
      { channelId, upload_type: type, text: caption },
      file,
      (pct) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === optimisticId ? { ...m, progress: pct } : m))
        );
      }
    )
      .then(async (res) => {
        const msg = res?.data?.message;
        if (!msg) {
          revokePreview();
          return;
        }

        const serverAttachments = msg.attachments || [];
        const serverUrl = serverAttachments[0]?.asset_url;
        const kind = getAttachmentKind(serverAttachments[0] || { type, mime_type: file.type, title: file.name });
        await preloadRemoteAttachment(serverUrl, kind);

        const normalized = normalizeApiMessage(msg, getMyId());
        if (!normalized) {
          revokePreview();
          return;
        }
        const mergedMessage = {
          ...normalized,
          uploading: false,
          progress: 100,
          previewUrl: null,
          attachments: serverAttachments.map((att) => ({
            ...att,
            preview_url: previewUrl,
            asset_url: att.asset_url || previewUrl,
          })),
        };

        setMessages((prev) =>
          integrateServerMessage(prev, mergedMessage, { replaceOptimisticId: optimisticId })
        );
        revokePreview();
      })
      .catch(() => {
        revokePreview();
        setMessages((prev) =>
          prev.map((m) =>
            m.id === optimisticId ? { ...m, uploading: false, failed: true } : m
          )
        );
      });

    // allow selecting same file again
    e.target.value = '';
  };

  const openImageViewer = (url, title = '') => {
    if (!url) return;
    setViewer({ open: true, url, title });
  };

  const closeImageViewer = () => {
    setViewer({ open: false, url: null, title: '' });
  };

  useEffect(() => {
    if (!messageMenu) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMessageMenu(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [messageMenu]);
  useEffect(() => {
    if (!viewer.open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeImageViewer();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [viewer.open]);

  const loadStreamChannelAndMessages = async (isCancelled) => {
    let cid = null;
    try {
      setLoadingMessages(true);
      setChannelId(null);
      setMessages([]);

      if (!classroom_id && !streamChannelIdProp) return;

      cid = streamChannelIdProp ? String(streamChannelIdProp) : null;
      if (!cid) {
        const dash = await classroomDashboard({ classroom_id });
        cid = dash?.data?.stream_channel_id;
      }
      if (!cid || isCancelled()) return;

      const resp = await getChannelMessagesApi({ channelId: String(cid) });
      const apiMessages = resp?.data?.messages || [];

      const myId = getMyId();
      const normalized = apiMessages
        .map((m) => normalizeApiMessage(m, myId))
        .filter(Boolean);

      if (isCancelled()) return;
      setMessages(normalized);

      setTimeout(scrollToBottom, 0);
    } catch (error) {
      console.warn('[Community] Failed to load initial messages:', error);
    } finally {
      if (!isCancelled()) {
        // Start the live listener after the REST snapshot. watch() supplies a
        // second snapshot to cover messages sent in between these operations.
        if (cid) setChannelId(String(cid));
        setLoadingMessages(false);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    loadStreamChannelAndMessages(() => cancelled);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroom_id, streamChannelIdProp]);

  // GetStream realtime listener (same flow as mobile Community chat).
  useEffect(() => {
    if (!channelId) return;
    let cancelled = false;
    let retryTimer = null;
    let retryDelay = 2000;

    if (streamUnsubscribeRef.current) {
      streamUnsubscribeRef.current();
      streamUnsubscribeRef.current = null;
    }

    const setupRealtime = async () => {
      try {
        const storedUser = getStoredUser();
        const myId = storedUser?.id ? String(storedUser.id) : null;
        const userName = storedUser?.name || storedUser?.full_name || storedUser?.username || 'User';
        const streamReg = await registerStreamUser();
        const streamToken = streamReg?.data?.token ? String(streamReg.data.token) : '';

        if (cancelled || !streamAppKey || !myId) return;
        if (!streamToken) throw new Error('GetStream token is missing');

        const resyncMessages = async () => {
          try {
            const response = await getChannelMessagesApi({ channelId });
            if (cancelled) return;
            const rows = (response?.data?.messages || [])
              .map((message) => normalizeApiMessage(message, myId))
              .filter(Boolean);
            setMessages((prev) => mergeByIdNewestLast(prev, rows));
          } catch (error) {
            console.warn('[Community] Failed to resync messages:', error);
          }
        };

        const sub = await subscribeCommunityChannelRealtime({
          apiKey: streamAppKey,
          userId: myId,
          userName,
          token: streamToken,
          channelId,
          handlers: {
            onRecovered: resyncMessages,
            onDeleted: (event) => {
              const id = event.message?.id;
              if (!id) return;
              setMessages((prev) => prev.filter((m) => String(m.id) !== String(id)));
            },
            onUpdated: (event) => {
              if (isDeletedStreamMessage(event.message)) {
                const id = event.message?.id;
                if (!id) return;
                setMessages((prev) => prev.filter((m) => String(m.id) !== String(id)));
                return;
              }
              const row = normalizeStreamEventMessage(event.message, myId);
              if (!row) return;
              setMessages((prev) => mergeByIdNewestLast(prev, [row]));
            },
            onNew: (event) => {
              const row = normalizeStreamEventMessage(event.message, myId);
              if (!row) return;
              const shouldAutoScroll = isNearBottom();
              setMessages((prev) => mergeByIdNewestLast(prev, [row]));
              if (shouldAutoScroll) setTimeout(scrollToBottom, 0);
            },
          },
        });

        if (cancelled) {
          sub.unsubscribe();
          return;
        }
        const snapshot = (sub.initialMessages || [])
          .map((message) => normalizeStreamEventMessage(message, myId))
          .filter(Boolean);
        setMessages((prev) => {
          const unseen = snapshot.filter((message) => !prev.some((row) => String(row.id) === String(message.id)));
          return mergeByIdNewestLast(prev, unseen);
        });
        streamUnsubscribeRef.current = sub.unsubscribe;
        retryDelay = 2000;
      } catch (e) {
        console.warn('[Community] Stream realtime init failed:', e);
        if (!cancelled) {
          retryTimer = setTimeout(setupRealtime, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 30000);
        }
      }
    };

    setupRealtime();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (streamUnsubscribeRef.current) {
        streamUnsubscribeRef.current();
        streamUnsubscribeRef.current = null;
      }
    };
  }, [channelId, streamAppKey]);

  return (
    <div
      className={"[&_.iconify]:tw-block [&_.iconify]:tw-opacity-[1] [&_.iconify]:tw-visible [&_.iconify_svg]:tw-block [&_.iconify_svg]:tw-opacity-[1] [&_.iconify_svg]:tw-visible [&_.ui-button_.iconify]:tw-text-[inherit] [&_.ui-button-primary_.iconify]:!tw-text-[#fff] [&_.ui-button-outline-secondary]:tw-bg-[transparent] [&_.community-send-btn:not(:disabled):not([disabled])]:!tw-bg-[var(--primary-color,_#3152e8)] [&_.community-send-btn:not(:disabled):not([disabled])]:!tw-border-[var(--primary-color,_#3152e8)] [&_.community-send-btn:not(:disabled):not([disabled])]:!tw-text-[#fff] [&_.community-send-btn:not(:disabled):not([disabled])_.iconify]:!tw-text-[#fff] [&_.community-send-btn:not(:disabled):not([disabled])_.iconify]:!tw-opacity-[1] [&_.community-send-btn:not(:disabled):not([disabled])_.iconify]:!tw-visible [&_.community-send-btn:disabled]:!tw-bg-[#e9ecef] [&_.community-send-btn:disabled]:!tw-border-[#ced4da] [&_.community-send-btn:disabled]:!tw-text-[var(--primary-color,_#3152e8)] [&_.community-send-btn:disabled]:!tw-opacity-[1] [&_.community-send-btn[disabled]]:!tw-bg-[#e9ecef] [&_.community-send-btn[disabled]]:!tw-border-[#ced4da] [&_.community-send-btn[disabled]]:!tw-text-[var(--primary-color,_#3152e8)] [&_.community-send-btn[disabled]]:!tw-opacity-[1] [&_.community-send-btn:disabled_.iconify]:!tw-text-[var(--primary-color,_#3152e8)] [&_.community-send-btn:disabled_.iconify]:!tw-opacity-[1] [&_.community-send-btn:disabled_.iconify]:!tw-visible [&_.community-send-btn[disabled]_.iconify]:!tw-text-[var(--primary-color,_#3152e8)] [&_.community-send-btn[disabled]_.iconify]:!tw-opacity-[1] [&_.community-send-btn[disabled]_.iconify]:!tw-visible [&_.community-media]:tw-w-[320px] [&_.community-media]:tw-max-w-full [&_.community-media]:tw-rounded-[12px] [&_.community-media]:tw-overflow-hidden [&_.community-media-img]:tw-w-full [&_.community-media-img]:tw-h-[200px] [&_.community-media-img]:tw-block [&_.community-media-img]:tw-object-cover [&_.community-media-img]:tw-rounded-[12px] [&_.community-media-img]:tw-bg-[#e9ecef] [&_.community-media-video]:tw-w-full [&_.community-media-video]:tw-h-full [&_.community-media-video]:tw-block [&_.community-media-video]:tw-object-contain [&_.community-media-video]:tw-rounded-[12px] [&_.community-media-video]:tw-bg-[#000] [&_.community-media-video-wrap]:tw-w-full [&_.community-media-video-wrap]:tw-h-[220px] [&_.community-media-video-wrap]:tw-overflow-hidden [&_.community-media-video-wrap]:tw-rounded-[12px] [&_.community-media-video-wrap]:tw-bg-[#000] [&_.community-media-video-wrap]:tw-flex [&_.community-media-video-wrap]:tw-items-center [&_.community-media-video-wrap]:tw-justify-center [&_.community-media-video-wrap_.common-bitmovin-player]:tw-w-full [&_.community-media-video-wrap_.common-bitmovin-player]:tw-h-full [&_.community-media-video-wrap_.common-bitmovin-player\\_\\_surface]:tw-w-full [&_.community-media-video-wrap_.common-bitmovin-player\\_\\_surface]:tw-h-full [&_.community-media-video-wrap_.bitmovinplayer-container]:!tw-w-full [&_.community-media-video-wrap_.bitmovinplayer-container]:!tw-h-full [&_.community-media-video-wrap_.bmpui-ui-uicontainer]:!tw-w-full [&_.community-media-video-wrap_.bmpui-ui-uicontainer]:!tw-h-full [&_.community-media-video-wrap_video]:!tw-w-full [&_.community-media-video-wrap_video]:!tw-h-full [&_.community-media-video-wrap_video]:!tw-max-h-[none] [&_.community-media-video-wrap_video]:!tw-object-contain [&_.community-media-video-wrap_video]:tw-bg-[#000] [&_.community-media-wrap]:tw-relative [&_.community-upload-overlay]:tw-absolute [&_.community-upload-overlay]:tw-inset-0 [&_.community-upload-overlay]:tw-flex [&_.community-upload-overlay]:tw-flex-col [&_.community-upload-overlay]:tw-items-center [&_.community-upload-overlay]:tw-justify-center [&_.community-upload-overlay]:tw-gap-[8px] [&_.community-upload-overlay]:tw-rounded-[12px] [&_.community-upload-overlay]:[background:rgba(0,_0,_0,_0.42)] [&_.community-upload-overlay]:tw-text-[#fff] [&_.community-upload-overlay]:tw-text-[12px] [&_.community-upload-overlay]:tw-font-semibold [&_.community-upload-overlay]:tw-pointer-events-none [&_.community-upload-spinner]:tw-w-[28px] [&_.community-upload-spinner]:tw-h-[28px] [&_.community-upload-spinner]:tw-border-[3px] [&_.community-upload-spinner]:tw-border-solid [&_.community-upload-spinner]:tw-border-[rgba(255,_255,_255,_0.35)] [&_.community-upload-spinner]:tw-border-t-[#fff] [&_.community-upload-spinner]:tw-rounded-[50%] [&_.community-upload-spinner]:tw-animate-[spin_0.8s_linear_infinite] [&_.community-audio-wrap]:tw-relative [&_.community-audio-wrap]:tw-w-[320px] [&_.community-audio-wrap]:tw-max-w-full [&_.community-media-bubble]:tw-p-[8px] [&_.community-message-bubble-own]:tw-select-none [&_.community-message-bubble-own]:[-webkit-user-select:none] [&_.community-message-bubble-own]:[touch-action:manipulation] [&_.community-message-bubble-own]:tw-cursor-pointer [&_.community-video-preview-btn]:tw-relative [&_.community-video-preview-btn]:tw-block [&_.community-video-preview-btn]:tw-w-full [&_.community-video-preview-btn]:tw-h-full [&_.community-video-preview-btn]:tw-p-0 [&_.community-video-preview-btn]:tw-border-0 [&_.community-video-preview-btn]:tw-bg-[transparent] [&_.community-video-preview-btn]:tw-cursor-pointer [&_.community-video-play-overlay]:tw-absolute [&_.community-video-play-overlay]:tw-inset-0 [&_.community-video-play-overlay]:tw-flex [&_.community-video-play-overlay]:tw-items-center [&_.community-video-play-overlay]:tw-justify-center [&_.community-video-play-overlay]:[background:rgba(0,_0,_0,_0.28)] [&_.community-video-play-overlay]:tw-text-[#fff] [&_.community-video-play-overlay]:tw-rounded-[12px] [&_.community-video-play-overlay]:tw-pointer-events-none [&_.community-doc-card]:tw-flex [&_.community-doc-card]:tw-items-center [&_.community-doc-card]:tw-gap-[12px] [&_.community-doc-card]:tw-w-[320px] [&_.community-doc-card]:tw-max-w-full [&_.community-doc-card]:tw-p-[10px_12px] [&_.community-doc-card]:tw-rounded-[12px] [&_.community-doc-card]:tw-no-underline [&_.community-doc-card]:tw-border-[1px] [&_.community-doc-card]:tw-border-solid [&_.community-doc-card]:tw-border-[rgba(0,_0,_0,_0.08)] [&_.community-doc-card]:[background:rgba(255,_255,_255,_0.92)] [&_.community-doc-card]:tw-text-[#212529] [&_.community-doc-card:hover]:tw-bg-[#fff] [&_.community-doc-card:hover]:tw-border-[rgba(13,_110,_253,_0.35)] [&_.community-doc-card.mine]:tw-border-[rgba(255,_255,_255,_0.22)] [&_.community-doc-card.mine]:[background:rgba(255,_255,_255,_0.12)] [&_.community-doc-card.mine]:tw-text-[#fff] [&_.community-doc-card.mine:hover]:[background:rgba(255,_255,_255,_0.16)] [&_.community-doc-card.mine:hover]:tw-border-[rgba(255,_255,_255,_0.35)] [&_.community-doc-icon]:tw-w-[40px] [&_.community-doc-icon]:tw-h-[40px] [&_.community-doc-icon]:tw-rounded-[10px] [&_.community-doc-icon]:tw-flex [&_.community-doc-icon]:tw-items-center [&_.community-doc-icon]:tw-justify-center [&_.community-doc-icon]:[background:rgba(13,_110,_253,_0.08)] [&_.community-doc-icon]:tw-text-[#0d6efd] [&_.community-doc-icon]:[flex:0_0_auto] [&_.community-doc-card.mine_.community-doc-icon]:[background:rgba(255,_255,_255,_0.2)] [&_.community-doc-card.mine_.community-doc-icon]:tw-text-[#fff] [&_.community-doc-meta]:tw-min-w-[0] [&_.community-doc-meta]:[flex:1_1_auto] [&_.community-doc-title]:tw-font-semibold [&_.community-doc-title]:tw-text-[14px] [&_.community-doc-title]:tw-whitespace-nowrap [&_.community-doc-title]:tw-overflow-hidden [&_.community-doc-title]:tw-text-ellipsis [&_.community-doc-sub]:tw-text-[12px] [&_.community-doc-sub]:tw-opacity-[0.75] [&_.community-doc-sub]:tw-whitespace-nowrap [&_.community-doc-sub]:tw-overflow-hidden [&_.community-doc-sub]:tw-text-ellipsis [&_.community-doc-action]:tw-w-[34px] [&_.community-doc-action]:tw-h-[34px] [&_.community-doc-action]:tw-rounded-[10px] [&_.community-doc-action]:tw-flex [&_.community-doc-action]:tw-items-center [&_.community-doc-action]:tw-justify-center [&_.community-doc-action]:[background:rgba(13,_110,_253,_0.08)] [&_.community-doc-action]:tw-text-[#0d6efd] [&_.community-doc-action]:[flex:0_0_auto] [&_.community-doc-card.mine_.community-doc-action]:[background:rgba(255,_255,_255,_0.2)] [&_.community-doc-card.mine_.community-doc-action]:tw-text-[#fff] [&_.community-delete-msg-btn]:tw-text-[#adb5bd] [&_.community-delete-msg-btn]:tw-leading-[1] [&_.community-delete-msg-btn]:tw-min-w-[auto] [&_.community-delete-msg-btn:hover]:tw-text-[#dc3545] [&_.community-edit-banner]:tw-flex [&_.community-edit-banner]:tw-items-center [&_.community-edit-banner]:tw-justify-between [&_.community-edit-banner]:tw-gap-[8px] [&_.community-edit-banner]:tw-p-[8px_12px_0] [&_.community-edit-banner]:tw-text-[13px] [&_.community-edit-banner]:tw-font-semibold [&_.community-edit-banner]:tw-text-[var(--primary-color,_#3152e8)] [&_.community-edit-cancel]:tw-border-0 [&_.community-edit-cancel]:tw-bg-[transparent] [&_.community-edit-cancel]:tw-text-[#6c757d] [&_.community-edit-cancel]:tw-text-[13px] [&_.community-edit-cancel]:tw-font-semibold [&_.community-edit-cancel]:tw-p-0 [&_.community-edit-cancel:hover]:tw-text-[#212529] [&_.community-message-editing]:[outline:2px_solid_rgba(49,_82,_232,_0.45)] [&_.community-action-overlay]:tw-absolute [&_.community-action-overlay]:tw-inset-0 [&_.community-action-overlay]:tw-z-[20] [&_.community-action-overlay]:tw-flex [&_.community-action-overlay]:tw-items-end [&_.community-action-overlay]:tw-justify-center [&_.community-action-overlay]:tw-p-[12px] [&_.community-action-overlay]:[background:rgba(15,_23,_42,_0.42)] [&_.community-action-overlay]:[backdrop-filter:blur(4px)] [&_.community-action-sheet]:tw-w-full [&_.community-action-sheet]:tw-max-w-[380px] [&_.community-action-sheet]:tw-p-[10px_12px_12px] [&_.community-action-sheet]:tw-rounded-[20px_20px_16px_16px] [&_.community-action-sheet]:tw-bg-[#fff] [&_.community-action-sheet]:tw-shadow-[0_18px_48px_rgba(15,_23,_42,_0.22)] [&_.community-action-handle]:tw-w-[36px] [&_.community-action-handle]:tw-h-[4px] [&_.community-action-handle]:tw-m-[2px_auto_12px] [&_.community-action-handle]:tw-rounded-[99px] [&_.community-action-handle]:tw-bg-[#dbe1ea] [&_.community-action-preview]:tw-m-[0_4px_12px] [&_.community-action-preview]:tw-p-[10px_12px] [&_.community-action-preview]:tw-rounded-[12px] [&_.community-action-preview]:tw-bg-[#f4f6fb] [&_.community-action-preview]:tw-text-[#334155] [&_.community-action-preview]:tw-text-[13px] [&_.community-action-preview]:tw-leading-[1.4] [&_.community-action-preview]:[display:-webkit-box] [&_.community-action-preview]:[-webkit-line-clamp:2] [&_.community-action-preview]:[-webkit-box-orient:vertical] [&_.community-action-preview]:tw-overflow-hidden [&_.community-action-list]:tw-flex [&_.community-action-list]:tw-flex-col [&_.community-action-list]:tw-gap-[4px] [&_.community-action-row]:tw-flex [&_.community-action-row]:tw-items-center [&_.community-action-row]:tw-gap-[12px] [&_.community-action-row]:tw-w-full [&_.community-action-row]:tw-p-[10px_10px] [&_.community-action-row]:tw-border-0 [&_.community-action-row]:tw-rounded-[14px] [&_.community-action-row]:tw-bg-[transparent] [&_.community-action-row]:tw-text-left [&_.community-action-row]:tw-text-[#0f172a] [&_.community-action-row:hover]:tw-bg-[#f4f6fb] [&_.community-action-row.danger:hover]:tw-bg-[#fff1f2] [&_.community-action-icon]:tw-flex [&_.community-action-icon]:tw-items-center [&_.community-action-icon]:tw-justify-center [&_.community-action-icon]:tw-w-[36px] [&_.community-action-icon]:tw-h-[36px] [&_.community-action-icon]:tw-rounded-[10px] [&_.community-action-icon]:[flex:0_0_auto] [&_.community-action-icon.edit]:[background:rgba(49,_82,_232,_0.1)] [&_.community-action-icon.edit]:tw-text-[var(--primary-color,_#3152e8)] [&_.community-action-icon.delete]:tw-bg-[#ffe4e6] [&_.community-action-icon.delete]:tw-text-[#e11d48] [&_.community-action-copy]:tw-flex [&_.community-action-copy]:tw-flex-col [&_.community-action-copy]:tw-min-w-[0] [&_.community-action-title]:tw-text-[14px] [&_.community-action-title]:[font-weight:650] [&_.community-action-title]:tw-leading-[1.2] [&_.community-action-row.danger_.community-action-title]:tw-text-[#e11d48] [&_.community-action-sub]:tw-mt-[2px] [&_.community-action-sub]:tw-text-[12px] [&_.community-action-sub]:tw-text-[#64748b] [&_.community-action-dismiss]:tw-w-full [&_.community-action-dismiss]:tw-mt-[8px] [&_.community-action-dismiss]:tw-p-[11px_12px] [&_.community-action-dismiss]:tw-border-0 [&_.community-action-dismiss]:tw-rounded-[12px] [&_.community-action-dismiss]:tw-bg-[#f1f5f9] [&_.community-action-dismiss]:tw-text-[#475569] [&_.community-action-dismiss]:tw-text-[14px] [&_.community-action-dismiss]:[font-weight:650] [&_.community-action-dismiss:hover]:tw-bg-[#e2e8f0] [&_.community-action-dismiss:hover]:tw-text-[#0f172a] [&_.community-confirm-icon]:tw-flex [&_.community-confirm-icon]:tw-items-center [&_.community-confirm-icon]:tw-justify-center [&_.community-confirm-icon]:tw-w-[44px] [&_.community-confirm-icon]:tw-h-[44px] [&_.community-confirm-icon]:tw-m-[4px_auto_10px] [&_.community-confirm-icon]:tw-rounded-[12px] [&_.community-confirm-icon]:tw-bg-[#ffe4e6] [&_.community-confirm-icon]:tw-text-[#e11d48] [&_.community-confirm-title]:tw-m-[0_4px_4px] [&_.community-confirm-title]:tw-text-center [&_.community-confirm-title]:tw-text-[16px] [&_.community-confirm-title]:tw-font-bold [&_.community-confirm-title]:tw-text-[#0f172a] [&_.community-confirm-sub]:tw-m-[0_4px_12px] [&_.community-confirm-sub]:tw-text-center [&_.community-confirm-sub]:tw-text-[13px] [&_.community-confirm-sub]:tw-text-[#64748b] [&_.community-confirm-actions]:tw-grid [&_.community-confirm-actions]:tw-grid-cols-[1fr_1fr] [&_.community-confirm-actions]:tw-gap-[8px] [&_.community-confirm-actions]:tw-mt-[4px] [&_.community-confirm-actions_.community-action-dismiss]:tw-mt-0 [&_.community-confirm-delete]:tw-p-[11px_12px] [&_.community-confirm-delete]:tw-border-0 [&_.community-confirm-delete]:tw-rounded-[12px] [&_.community-confirm-delete]:tw-bg-[#e11d48] [&_.community-confirm-delete]:tw-text-[#fff] [&_.community-confirm-delete]:tw-text-[14px] [&_.community-confirm-delete]:[font-weight:650] [&_.community-confirm-delete:hover]:tw-bg-[#be123c] min-[768px]:[&_.community-action-overlay]:tw-items-center min-[768px]:[&_.community-action-sheet]:tw-rounded-[18px] min-[768px]:[&_.community-action-handle]:tw-hidden community-chat tw-flex tw-flex-col"}
      style={{ height: 'calc(100vh - 260px)', minHeight: 420, position: 'relative', overflow: 'hidden' }}
    >
      {/* Hidden file input (used by upload modal choices) */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        accept={getAcceptForUploadType(pendingUploadType)}
        onChange={onFilePicked}
      />

      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-px-4 tw-py-2 tw-bg-white">
        <div className="tw-flex tw-items-center tw-gap-4">
          <div className="[@mediascreen_and_(max-width:_768px)]:tw-hidden tw-mt-auto tw-mb-auto profile-chat">
            {headerProfile.imageUrl ? (
              <img
                src={headerProfile.imageUrl}
                alt={headerProfile.name}
                className="tw-rounded-full"
                style={{ width: 44, height: 44, objectFit: 'cover' }}
              />
            ) : (
              <div
                className="tw-rounded-full tw-bg-slate-50 tw-flex tw-items-center tw-justify-center"
                style={{ width: 44, height: 44 }}
              >
                <Icon icon="fluent:people-community-24-regular" width="22" height="22" />
              </div>
            )}
          </div>
          <div>
            <div className="![font-weight:var(--font-weight-semibold)] tw-font-semibold">{headerProfile.name}</div>
            <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm">Long-press your message to edit or delete</div>
          </div>
        </div>

        <div />
      </div>

      {/* Body */}
      <div
        ref={listRef}
        className="tw-grow tw-px-4 tw-py-4"
        style={{ overflowY: 'auto', background: '#f8f9fa' }}
      >
        {loadingMessages && (
          <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-center tw-text-slate-500 tw-text-sm tw-mb-4">Loading messages…</div>
        )}
        {!loadingMessages && messages.length === 0 && (
          <NoData
            message="No messages yet"
            description="Start the conversation by sending the first message."
            icon="mdi:chat-outline"
            compact
          />
        )}
        {messages.map((m) => {
          const hasText = Boolean(m.text?.trim());
          const hasAttachments = Array.isArray(m.attachments) && m.attachments.length > 0;
          const canDelete =
            m.mine &&
            !m.uploading &&
            !String(m.id).startsWith('local-') &&
            !String(m.id).startsWith('upload-');
          const canEdit = canDelete && hasText;

          return (
          <div
            key={m.id}
            className="tw-flex tw-mb-4 tw-w-full"
          >
            <div
              className={`tw-flex tw-gap-2   ${m.mine ? "tw-ms-auto tw-flex-row-reverse" : "tw-me-auto tw-flex-row"}`}
              style={{ maxWidth: 720 }}
            >
              {/* avatar */}
              <div
                className="tw-flex tw-items-center tw-justify-center tw-rounded-full"
                style={{
                  width: 36,
                  height: 36,
                  flex: '0 0 auto',
                  background: m.mine ? '#0d6efd' : '#e9ecef',
                  color: m.mine ? '#fff' : '#495057',
                  fontWeight: 700,
                  fontSize: 12,
                }}
                title={m.senderName}
              >
                {initials(m.senderName)}
              </div>

              {/* message */}
              <div style={{ minWidth: 0 }}>
                <div
                  className={`tw-flex tw-items-center   ${m.mine ? "tw-justify-end" : "tw-justify-start"}`}
                  style={{ gap: 8 }}
                >
                  <div className="![font-weight:var(--font-weight-semibold)] !tw-text-[12px] tw-text-sm tw-font-semibold" style={{ color: '#6c757d' }}>
                    {m.senderName}
                  </div>
                </div>

                <div
                  className={`tw-rounded-xl tw-shadow-sm   ${m.mine ? "!tw-bg-[var(--primary-color)] tw-bg-brand-600 tw-text-white" : "tw-bg-white"}      ${
                    hasAttachments && !hasText ? "community-media-bubble" : "tw-p-2"
                  }      ${canDelete ? "community-message-bubble-own" : ""}      ${
                    editingMessage?.id === m.id ? "community-message-editing" : ""
                  }`}
                  style={{ maxWidth: 560, width: 'fit-content' }}
                  title={
                    canEdit
                      ? 'Long-press to edit or delete'
                      : canDelete
                        ? 'Long-press to delete'
                        : undefined
                  }
                  {...(canDelete
                    ? createLongPressHandlers(() => openMessageActions(m))
                    : {})}
                >
                  {hasText ? (
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>
                  ) : null}

                  {hasAttachments && (
                    <div className={hasText ? "tw-mt-2" : ""}>
                      {m.attachments.map((a, idx) => (
                        <CommunityMessageAttachment
                          key={`${m.id}-att-${idx}`}
                          attachment={a}
                          mine={m.mine}
                          uploading={Boolean(m.uploading)}
                          progress={m.progress}
                          onImageOpen={openImageViewer}
                        />
                      ))}
                    </div>
                  )}

                  {m.failed && (
                    <div className="!tw-text-[var(--warning-color)] !tw-text-[12px] tw-text-sm tw-mt-2 tw-text-amber-600">Upload failed. Try again.</div>
                  )}

                  <div
                    className="!tw-text-[12px] tw-text-sm tw-mt-1"
                    style={{
                      textAlign: 'right',
                      opacity: 0.85,
                      color: m.mine ? 'rgba(255,255,255,0.85)' : '#adb5bd',
                    }}
                  >
                    {m.edited ? 'Edited · ' : ''}
                    {formatTime(m.createdAt)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Full screen image viewer (custom modal) */}
      {viewer.open && (
        <div
          className="tw-fixed tw-inset-0 tw-z-[2000] [background:rgba(0,_0,_0,_0.78)] tw-flex tw-flex-col community-viewer"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeImageViewer();
          }}
        >
          <div className="tw-h-[56px] tw-p-[10px_14px] tw-flex tw-items-center tw-justify-between tw-text-[#fff] [background:rgba(0,_0,_0,_0.55)] [backdrop-filter:blur(6px)] community-viewer-header">
            <div className="tw-font-semibold tw-text-[14px] tw-max-w-[70vw] tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis community-viewer-title" title={viewer.title || 'Image'}>
              {viewer.title || 'Image'}
            </div>
            <div className="tw-flex tw-items-center tw-gap-[10px] community-viewer-actions">
              <a
                className={"tw-w-[40px] tw-h-[40px] tw-rounded-[10px] tw-inline-flex tw-items-center tw-justify-center tw-text-[#fff] tw-border-[1px] tw-border-solid tw-border-[rgba(255,_255,_255,_0.22)] [background:rgba(255,_255,_255,_0.08)] tw-no-underline [&:hover]:[background:rgba(255,_255,_255,_0.14)] [&:active]:[transform:translateY(1px)] community-viewer-btn"}
                href={viewer.url || undefined}
                target="_blank"
                rel="noreferrer"
                title="Open in new tab"
              >
                <Icon icon="fluent:open-24-regular" width="18" height="18" />
              </a>
              <a
                className={"tw-w-[40px] tw-h-[40px] tw-rounded-[10px] tw-inline-flex tw-items-center tw-justify-center tw-text-[#fff] tw-border-[1px] tw-border-solid tw-border-[rgba(255,_255,_255,_0.22)] [background:rgba(255,_255,_255,_0.08)] tw-no-underline [&:hover]:[background:rgba(255,_255,_255,_0.14)] [&:active]:[transform:translateY(1px)] community-viewer-btn"}
                href={viewer.url || undefined}
                download
                title="Download"
                onClick={(e) => {
                  if (!viewer.url) e.preventDefault();
                }}
              >
                <Icon icon="fluent:arrow-download-24-regular" width="18" height="18" />
              </a>
              <button
                type="button"
                className={"tw-w-[40px] tw-h-[40px] tw-rounded-[10px] tw-inline-flex tw-items-center tw-justify-center tw-text-[#fff] tw-border-[1px] tw-border-solid tw-border-[rgba(255,_255,_255,_0.22)] [background:rgba(255,_255,_255,_0.08)] tw-no-underline [&:hover]:[background:rgba(255,_255,_255,_0.14)] [&:active]:[transform:translateY(1px)] community-viewer-btn"}
                onClick={closeImageViewer}
                title="Close (Esc)"
              >
                <Icon icon="fluent:dismiss-24-regular" width="18" height="18" />
              </button>
            </div>
          </div>

          <div className="[flex:1_1_auto] tw-flex tw-items-center tw-justify-center tw-p-[18px] community-viewer-body">
            <img
              src={viewer.url}
              alt={viewer.title || 'Image'}
              className="tw-max-w-[min(1100px,_100%)] tw-max-h-full tw-w-auto tw-h-auto tw-object-contain tw-rounded-[14px] tw-shadow-[0_18px_60px_rgba(0,_0,_0,_0.55)] tw-bg-[#000] community-viewer-image"
              draggable={false}
            />
          </div>
        </div>
      )}

      {/* Input */}
      <div className="tw-border-t tw-bg-white">
        {editingMessage ? (
          <div className="community-edit-banner">
            <span>Editing message</span>
            <button type="button" className="community-edit-cancel" onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        ) : null}
        <div className="tw-relative tw-flex tw-items-center tw-gap-[8px] tw-p-[8px] message-input-container">
          <button
            type="button"
            className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] ui-button tw-flex tw-items-center tw-justify-center"}
            style={{
              width: 44,
              height: 44,
              minWidth: 44,
              borderRadius: 12,
              flex: '0 0 auto',
              padding: 0,
              background: '#fff',
              border: '1px solid #ced4da',
              color: editingMessage ? '#adb5bd' : '#0d6efd',
              opacity: editingMessage ? 0.55 : 1,
            }}
            title="Attach"
            disabled={Boolean(editingMessage)}
            onClick={() => {
              if (editingMessage) return;
              setIsUploadModalOpen(true);
            }}
          >
            <Icon
              icon="fluent:add-24-regular"
              width="20"
              height="20"
              style={{ color: 'currentColor', display: 'block' }}
            />
          </button>

          <textarea
            ref={inputRef}
            className={"[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input"}
            rows={1}
            placeholder={editingMessage ? 'Edit your message…' : 'Type a message...'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            style={{
              resize: 'none',
              flex: '1 1 auto',
              minWidth: 0,
              lineHeight: '1.25rem',
            }}
          />
          <div className="tw-flex tw-items-center tw-justify-center tw-p-[4px] send-button-container">
            <button
              type="button"
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] ui-button community-send-btn tw-flex tw-items-center tw-justify-center"}
              onClick={handleSend}
              disabled={
                !input.trim() ||
                (Boolean(editingMessage) &&
                  input.trim() === String(editingMessage?.text || '').trim())
              }
              title={editingMessage ? 'Save' : 'Send'}
              style={{
                width: 44,
                height: 44,
                minWidth: 44,
                borderRadius: 12,
                flex: '0 0 auto',
                padding: 0,
              }}
            >
              <Icon
                icon="fluent:send-24-regular"
                width="20"
                height="20"
                style={{ display: 'block' }}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Upload chooser modal */}
      {isUploadModalOpen && (
        <>
          <Dialog show onHide={() => setIsUploadModalOpen(false)}>
                <Dialog.Header closeButton>
                  <Dialog.Title >Upload</Dialog.Title>

                </Dialog.Header>
                <Dialog.Body >
                  <div className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] ![border-style:dashed] tw-bg-[#f8f9fa] tw-border-dashed tw-border-[#dee2e6] tw-text-center tw-cursor-pointer [transition:all_0.3s_ease] [&:hover]:!tw-border-[var(--ui-primary)] [&:hover]:tw-bg-[#f0f8ff] [&.dragging]:tw-border-[var(--primary-color)] [&.dragging]:tw-bg-[#e6f3ff] [&.dragging]:[transform:scale(1.02)] upload-area tw-border tw-rounded-xl tw-p-4"}>
                    <div className={"tw-flex tw-flex-wrap tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] [--grid-x:.5rem] [--grid-y:.5rem] ui-row tw-gap-grid-2"}>
                      <div className="tw-min-w-[0px] ui-column tw-w-1/2">
                        <button
                          type="button"
                          className={"tw-flex tw-flex-col tw-items-center tw-bg-[none] tw-border-0 tw-p-[12px_8px] tw-cursor-pointer [transition:all_0.2s_ease] tw-rounded-[8px] [&:hover:not(:disabled)]:tw-bg-[rgba(13,_110,_253,_0.1)] [&_.icon-wrapper]:tw-flex [&_.icon-wrapper]:tw-items-center [&_.icon-wrapper]:tw-justify-center [&_.icon-wrapper]:tw-w-[48px] [&_.icon-wrapper]:tw-h-[48px] [&_.icon-wrapper]:tw-rounded-[50%] [&_.icon-wrapper]:tw-bg-[#0d6efd] [&_.icon-wrapper]:tw-mb-[8px] [&_.icon-wrapper]:tw-text-[white] [&_.icon-wrapper]:[transition:all_0.2s_ease] [&:hover:not(:disabled)_.icon-wrapper]:tw-bg-[#0b5ed7] [&:hover:not(:disabled)_.icon-wrapper]:[transform:translateY(-2px)] [&_span]:tw-text-[14px] [&_span]:tw-text-[#495057] [&:disabled]:tw-opacity-[0.5] [&:disabled]:tw-cursor-not-allowed upload-type-btn tw-w-full"}
                          onClick={() => handleChooseUploadType('image')}
                        >
                          <span className="icon-wrapper">
                            <Icon icon="fluent:image-24-regular" width="24" height="24" />
                          </span>
                          <span>Image</span>
                        </button>
                      </div>
                      <div className="tw-min-w-[0px] ui-column tw-w-1/2">
                        <button
                          type="button"
                          className={"tw-flex tw-flex-col tw-items-center tw-bg-[none] tw-border-0 tw-p-[12px_8px] tw-cursor-pointer [transition:all_0.2s_ease] tw-rounded-[8px] [&:hover:not(:disabled)]:tw-bg-[rgba(13,_110,_253,_0.1)] [&_.icon-wrapper]:tw-flex [&_.icon-wrapper]:tw-items-center [&_.icon-wrapper]:tw-justify-center [&_.icon-wrapper]:tw-w-[48px] [&_.icon-wrapper]:tw-h-[48px] [&_.icon-wrapper]:tw-rounded-[50%] [&_.icon-wrapper]:tw-bg-[#0d6efd] [&_.icon-wrapper]:tw-mb-[8px] [&_.icon-wrapper]:tw-text-[white] [&_.icon-wrapper]:[transition:all_0.2s_ease] [&:hover:not(:disabled)_.icon-wrapper]:tw-bg-[#0b5ed7] [&:hover:not(:disabled)_.icon-wrapper]:[transform:translateY(-2px)] [&_span]:tw-text-[14px] [&_span]:tw-text-[#495057] [&:disabled]:tw-opacity-[0.5] [&:disabled]:tw-cursor-not-allowed upload-type-btn tw-w-full"}
                          onClick={() => handleChooseUploadType('video')}
                        >
                          <span className="icon-wrapper">
                            <Icon icon="fluent:video-24-regular" width="24" height="24" />
                          </span>
                          <span>Video</span>
                        </button>
                      </div>
                      <div className="tw-min-w-[0px] ui-column tw-w-1/2">
                        <button
                          type="button"
                          className={"tw-flex tw-flex-col tw-items-center tw-bg-[none] tw-border-0 tw-p-[12px_8px] tw-cursor-pointer [transition:all_0.2s_ease] tw-rounded-[8px] [&:hover:not(:disabled)]:tw-bg-[rgba(13,_110,_253,_0.1)] [&_.icon-wrapper]:tw-flex [&_.icon-wrapper]:tw-items-center [&_.icon-wrapper]:tw-justify-center [&_.icon-wrapper]:tw-w-[48px] [&_.icon-wrapper]:tw-h-[48px] [&_.icon-wrapper]:tw-rounded-[50%] [&_.icon-wrapper]:tw-bg-[#0d6efd] [&_.icon-wrapper]:tw-mb-[8px] [&_.icon-wrapper]:tw-text-[white] [&_.icon-wrapper]:[transition:all_0.2s_ease] [&:hover:not(:disabled)_.icon-wrapper]:tw-bg-[#0b5ed7] [&:hover:not(:disabled)_.icon-wrapper]:[transform:translateY(-2px)] [&_span]:tw-text-[14px] [&_span]:tw-text-[#495057] [&:disabled]:tw-opacity-[0.5] [&:disabled]:tw-cursor-not-allowed upload-type-btn tw-w-full"}
                          onClick={() => handleChooseUploadType('audio')}
                        >
                          <span className="icon-wrapper">
                            <Icon icon="fluent:speaker-2-24-regular" width="24" height="24" />
                          </span>
                          <span>Audio</span>
                        </button>
                      </div>
                      <div className="tw-min-w-[0px] ui-column tw-w-1/2">
                        <button
                          type="button"
                          className={"tw-flex tw-flex-col tw-items-center tw-bg-[none] tw-border-0 tw-p-[12px_8px] tw-cursor-pointer [transition:all_0.2s_ease] tw-rounded-[8px] [&:hover:not(:disabled)]:tw-bg-[rgba(13,_110,_253,_0.1)] [&_.icon-wrapper]:tw-flex [&_.icon-wrapper]:tw-items-center [&_.icon-wrapper]:tw-justify-center [&_.icon-wrapper]:tw-w-[48px] [&_.icon-wrapper]:tw-h-[48px] [&_.icon-wrapper]:tw-rounded-[50%] [&_.icon-wrapper]:tw-bg-[#0d6efd] [&_.icon-wrapper]:tw-mb-[8px] [&_.icon-wrapper]:tw-text-[white] [&_.icon-wrapper]:[transition:all_0.2s_ease] [&:hover:not(:disabled)_.icon-wrapper]:tw-bg-[#0b5ed7] [&:hover:not(:disabled)_.icon-wrapper]:[transform:translateY(-2px)] [&_span]:tw-text-[14px] [&_span]:tw-text-[#495057] [&:disabled]:tw-opacity-[0.5] [&:disabled]:tw-cursor-not-allowed upload-type-btn tw-w-full"}
                          onClick={() => handleChooseUploadType('document')}
                        >
                          <span className="icon-wrapper">
                            <Icon icon="fluent:document-24-regular" width="24" height="24" />
                          </span>
                          <span>Document</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </Dialog.Body>
                <Dialog.Footer >
                  <button
                    type="button"
                    className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-outline-secondary"}
                    onClick={() => setIsUploadModalOpen(false)}
                  >
                    Cancel
                  </button>
                </Dialog.Footer>
              </Dialog>
        </>
      )}

      {messageMenu && (
        <div
          className="community-action-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={messageMenu.step === 'delete' ? 'Delete message' : 'Message actions'}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setMessageMenu(null);
          }}
        >
          <div className="community-action-sheet">
            <div className="community-action-handle" />
            {messageMenu.step === 'delete' ? (
              <>
                <div className="community-confirm-icon">
                  <Icon icon="fluent:delete-24-regular" width="22" height="22" />
                </div>
                <div className="community-confirm-title">Delete this message?</div>
                <div className="community-confirm-sub">This can't be undone.</div>
                <div className="community-action-preview">
                  {String(messageMenu.message?.text || '').trim()
                    ? String(messageMenu.message.text).trim()
                    : 'Attachment'}
                </div>
                <div className="community-confirm-actions">
                  <button
                    type="button"
                    className="community-action-dismiss"
                    onClick={() => setMessageMenu((prev) => (prev ? { ...prev, step: 'menu' } : null))}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="community-confirm-delete"
                    onClick={() => handleDeleteMessage(messageMenu.id)}
                  >
                    Delete
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="community-action-preview">
                  {String(messageMenu.message?.text || '').trim()
                    ? String(messageMenu.message.text).trim()
                    : 'Attachment'}
                </div>
                <div className="community-action-list">
                  {messageMenu.canEdit ? (
                    <button
                      type="button"
                      className="community-action-row"
                      onClick={() => startEditMessage(messageMenu.message)}
                    >
                      <span className="community-action-icon edit">
                        <Icon icon="fluent:edit-24-regular" width="18" height="18" />
                      </span>
                      <span className="community-action-copy">
                        <span className="community-action-title">Edit</span>
                        <span className="community-action-sub">Change this text</span>
                      </span>
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="community-action-row danger"
                    onClick={() => setMessageMenu((prev) => (prev ? { ...prev, step: 'delete' } : null))}
                  >
                    <span className="community-action-icon delete">
                      <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                    </span>
                    <span className="community-action-copy">
                      <span className="community-action-title">Delete</span>
                      <span className="community-action-sub">Remove this message</span>
                    </span>
                  </button>
                </div>
                <button
                  type="button"
                  className="community-action-dismiss"
                  onClick={() => setMessageMenu(null)}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Cummunity;

